import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { api, errMsg, formatDate, rupiah } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { AdminHead, FilterSelect, Table, Td } from "@/components/admin/AdminUI";
import { StatusBadge } from "@/components/StatusBadge";
import { PageLoader } from "@/components/layout/RequireAuth";

const ROLE = { pelanggan: "Pelanggan", penyedia: "Penyedia", admin: "Admin" };

export function AdminUsers() {
  const [f, setF] = useState({ q: "", role: "", status: "", verified: "" });
  const [rows, setRows] = useState(null);
  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ""));
    api.get("/admin/users", { params }).then((r) => setRows(r.data));
  }, [f]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div data-testid="admin-users">
      <AdminHead title="Pengguna & Penyedia" />
      <div className="mb-4 flex flex-wrap gap-2">
        <Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Cari nama atau email" className="h-10 max-w-xs rounded-xl" data-testid="users-search-input" />
        <FilterSelect value={f.role} onChange={(v) => setF({ ...f, role: v })} testId="users-role-filter" options={[["", "Semua peran"], ["pelanggan", "Pelanggan"], ["penyedia", "Penyedia"], ["admin", "Admin"]]} />
        <FilterSelect value={f.status} onChange={(v) => setF({ ...f, status: v })} testId="users-status-filter" options={[["", "Semua status"], ["aktif", "Aktif"], ["ditangguhkan", "Ditangguhkan"]]} />
        <FilterSelect value={f.verified} onChange={(v) => setF({ ...f, verified: v })} testId="users-verified-filter" options={[["", "Semua verifikasi"], ["true", "Terverifikasi"], ["false", "Belum terverifikasi"]]} />
      </div>
      {rows === null ? <PageLoader /> : (
        <Table cols={["Nama", "Email", "Peran", "Status", "Kredit", "Verifikasi", ""]} testId="users-table" rows={rows.map((u) => (
          <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
            <Td className="font-semibold text-stone-900">{u.provider?.business_name || u.name}</Td>
            <Td>{u.email}</Td>
            <Td>{ROLE[u.role] || "Belum memilih"}</Td>
            <Td>{u.suspended ? <span className="font-semibold text-rose-700">Ditangguhkan</span> : "Aktif"}</Td>
            <Td>{u.provider ? u.provider.credits : "-"}</Td>
            <Td>{u.provider ? (u.provider.verified ? "Terverifikasi" : u.provider.verification?.status || "belum") : "-"}</Td>
            <Td><Link to={`/admin/pengguna/${u.user_id}`} className="font-semibold text-teal-700" data-testid={`user-detail-link-${u.user_id}`}>Detail</Link></Td>
          </tr>
        ))} />
      )}
    </div>
  );
}

export function AdminUserDetail() {
  const { id } = useParams();
  const [u, setU] = useState(null);
  const [adj, setAdj] = useState({ delta: "", reason: "" });
  const load = useCallback(() => api.get(`/admin/users/${id}`).then((r) => setU(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  const call = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!u) return <PageLoader />;
  const p = u.provider;

  return (
    <div data-testid="admin-user-detail">
      <Link to="/admin/pengguna" className="mb-3 inline-flex items-center gap-1 text-sm text-stone-500"><ArrowLeft className="h-4 w-4" /> Pengguna</Link>
      <AdminHead title={p?.business_name || u.name} desc={`${u.email} · ${ROLE[u.role] || "-"} · bergabung ${formatDate(u.created_at)}`} />
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="card-soft space-y-3 p-5">
          <p className="text-sm font-bold text-stone-900">Status akun</p>
          <label className="flex items-center justify-between text-sm">Ditangguhkan
            <Switch checked={!!u.suspended} disabled={u.role === "admin"} data-testid="suspend-switch"
              onCheckedChange={(v) => call(() => api.post(`/admin/users/${id}/suspend`, { value: v }), v ? "Akun ditangguhkan" : "Akun diaktifkan")} />
          </label>
          <p className="text-xs text-stone-500">Akun yang ditangguhkan tidak dapat masuk atau memakai API.</p>
          <p className="text-sm">Telepon: {u.phone || "-"}</p>
        </div>
        {p && (
          <div className="card-soft space-y-3 p-5">
            <p className="text-sm font-bold text-stone-900">Kredit: <span data-testid="admin-user-credits">{p.credits}</span></p>
            <Input type="number" value={adj.delta} onChange={(e) => setAdj({ ...adj, delta: e.target.value })} placeholder="mis. 5 atau -2" className="rounded-xl" data-testid="credit-adjust-delta" />
            <Input value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} placeholder="Alasan" className="rounded-xl" data-testid="credit-adjust-reason" />
            <Button className="w-full rounded-xl bg-teal-700 hover:bg-teal-800" data-testid="credit-adjust-submit"
              onClick={() => call(() => api.post(`/admin/users/${id}/credits`, { delta: Number(adj.delta), reason: adj.reason }).then(() => setAdj({ delta: "", reason: "" })), "Kredit disesuaikan")}>Simpan penyesuaian</Button>
          </div>
        )}
        {p && (
          <div className="card-soft space-y-3 p-5">
            <p className="text-sm font-bold text-stone-900">Verifikasi</p>
            <label className="flex items-center justify-between text-sm">Lencana Terverifikasi
              <Switch checked={p.verified} data-testid="verified-switch" onCheckedChange={(v) => call(() => api.post(`/admin/users/${id}/verified`, { value: v }), "Status verifikasi diperbarui")} />
            </label>
            <p className="text-xs text-stone-500">Status pengajuan: {p.verification?.status || "belum"}</p>
            <p className="text-xs text-stone-500">Bank: {p.bank_name || "-"} {p.bank_account_number} a.n. {p.bank_account_name || "-"}</p>
            <Link to={`/penyedia/${p.slug}`} className="text-xs font-semibold text-teal-700">Profil publik</Link>
          </div>
        )}
      </div>
      <h2 className="mb-3 mt-8 font-bold text-stone-900">Pesanan</h2>
      <Table cols={["Pesanan", "Status", "Harga", "Tanggal"]} testId="user-orders-table" rows={u.orders.map((o) => (
        <tr key={o.order_id}><Td><Link to={`/admin/pesanan/${o.order_id}`} className="font-semibold text-teal-700">{o.title}</Link></Td><Td><StatusBadge status={o.status} /></Td><Td>{rupiah(o.price)}</Td><Td>{formatDate(o.created_at)}</Td></tr>
      ))} />
      {u.requests.length > 0 && (<>
        <h2 className="mb-3 mt-8 font-bold text-stone-900">Permintaan</h2>
        <Table cols={["Kategori", "Kota", "Status", "Penawaran", "Tanggal"]} testId="user-requests-table" rows={u.requests.map((r) => (
          <tr key={r.request_id}><Td>{r.category_name}</Td><Td>{r.city_name}</Td><Td><StatusBadge status={r.status} /></Td><Td>{r.quote_count}</Td><Td>{formatDate(r.created_at)}</Td></tr>
        ))} />
      </>)}
      {p && (<>
        <h2 className="mb-3 mt-8 font-bold text-stone-900">Riwayat kredit</h2>
        <Table cols={["Catatan", "Perubahan", "Tanggal"]} testId="user-ledger-table" rows={u.ledger.map((e) => (
          <tr key={e.entry_id}><Td>{e.note}</Td><Td className={e.delta > 0 ? "font-bold text-emerald-700" : "font-bold"}>{e.delta > 0 ? `+${e.delta}` : e.delta}</Td><Td>{formatDate(e.created_at)}</Td></tr>
        ))} />
      </>)}
    </div>
  );
}
